import { useRef, useState } from 'react'
import type { ChangeEvent, ReactNode } from 'react'
import { Check, Loader2, Play, RefreshCw, Trash2, Upload, X } from 'lucide-react'
import { Banner, Button, FileDropzone } from '@shared/design-system/ui'
import { apiAuthHeaders, apiUrl } from '@shared/api/http'
import { AppError } from '@shared/api/error'
import { toast } from '@shared/lib/toast/toast-store'
import { HlsVideoPlayer } from '@shared/ui/HlsVideoPlayer'
import type { MembersLesson } from '../model/members-area.contract'
import {
  MAX_VIDEO_BYTES,
  VIDEO_ACCEPT,
  useDeleteMembersVideo,
  useRetryMembersVideo,
  useUploadMembersVideo,
} from '../model/members-area.mutations'
import { formatFileSize, formatVideoDuration } from '../model/members-area.presentation'
import { videoProcessStepState, type VideoBusyPhase } from '../model/video-upload-progress'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { ProgressBar } from './ProgressBar'
import {
  fileMeta,
  videoActions,
  videoField,
  videoStage,
  videoStageBar,
  videoStageBars,
  videoStageFile,
  videoStageHint,
  videoStageIcon,
  videoStageMark,
  videoStageMeter,
  videoStagePercent,
  videoStagePulse,
  videoStageScan,
  videoStageSpin,
  videoStageStep,
  videoStageStepDot,
  videoStageSteps,
  videoStageTick,
  videoStageTitle,
  videoStatusRow,
  visuallyHiddenInput,
} from './members-area.css'

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm']

function videoErrorMessage(error: unknown) {
  if (error instanceof AppError && error.status === 503) return 'Vídeo indisponível neste ambiente.'
  if (error instanceof AppError || error instanceof Error) return error.message
  return 'Não foi possível enviar o vídeo.'
}

const PROCESS_STEPS = [
  { id: 'upload', label: 'Envio' },
  { id: 'process', label: 'Processamento' },
  { id: 'ready', label: 'Pronto' },
] as const

function VideoBusyStage({
  phase,
  title,
  fileName,
  hint,
  progress,
  action,
}: {
  phase: VideoBusyPhase
  title: string
  fileName: string
  hint: string
  progress?: number
  action?: ReactNode
}) {
  const shownProgress = phase === 'uploading' ? progress : undefined
  const processing = phase === 'processing'
  return (
    <div className={videoStage} role="status" aria-live="polite" aria-busy="true">
      {processing ? <span className={videoStageScan} aria-hidden /> : null}
      <span className={videoStageMark}>
        {processing ? <span className={videoStagePulse} aria-hidden /> : null}
        <span className={videoStageIcon}>
          {processing ? (
            <Play size={22} fill="currentColor" aria-hidden />
          ) : (
            <Loader2 size={22} className={videoStageSpin} aria-hidden />
          )}
        </span>
      </span>
      {processing ? (
        <span className={videoStageBars} aria-hidden>
          <span className={videoStageTick} />
          <span className={videoStageTick} />
          <span className={videoStageTick} />
          <span className={videoStageTick} />
          <span className={videoStageTick} />
        </span>
      ) : null}
      <p className={videoStageTitle}>{title}</p>
      {processing ? null : (
        <p className={videoStageFile} title={fileName}>
          {fileName}
        </p>
      )}
      {shownProgress !== undefined ? (
        <div className={videoStageMeter}>
          <ProgressBar
            value={shownProgress}
            label={`Envio de ${fileName}`}
            className={videoStageBar}
          />
          <span className={videoStagePercent}>{shownProgress}%</span>
        </div>
      ) : null}
      {processing ? null : (
        <ol className={videoStageSteps}>
          {PROCESS_STEPS.map((step) => {
            const state = videoProcessStepState(phase, step.id)
            return (
              <li
                key={step.id}
                className={videoStageStep({ state })}
                aria-current={state === 'current' ? 'step' : undefined}
              >
                {state === 'done' ? (
                  <Check size={11} aria-hidden />
                ) : (
                  <span className={videoStageStepDot({ state })} aria-hidden />
                )}
                {step.label}
              </li>
            )
          })}
        </ol>
      )}
      {processing ? null : <p className={videoStageHint}>{hint}</p>}
      {action}
    </div>
  )
}

function validateVideo(file: File) {
  const name = file.name.toLowerCase()
  if (!VIDEO_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'Envie o vídeo em MP4, MOV ou WEBM.'
  }
  if (file.size > MAX_VIDEO_BYTES) return `"${file.name}" passa de 1 GB.`
  return null
}

export function LessonVideoField({
  productId,
  lesson,
  onFileSelected,
  onLessonReady,
  onUploadingChange,
}: {
  productId: string
  lesson: MembersLesson | null
  onFileSelected?: (file: File) => Promise<string>
  onLessonReady?: (lessonId: string) => void
  onUploadingChange?: (uploading: boolean) => void
}) {
  const upload = useUploadMembersVideo(productId)
  const retry = useRetryMembersVideo(productId)
  const remove = useDeleteMembersVideo(productId)
  const [progress, setProgress] = useState<{ name: string; value: number } | null>(null)
  const [preparingName, setPreparingName] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<'replace' | 'remove' | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  const video = lesson?.video ?? null
  const lessonId = lesson?.id

  async function sendFile(targetLessonId: string, file: File) {
    const controller = new AbortController()
    abortRef.current = controller
    onUploadingChange?.(true)
    setProgress({ name: file.name, value: 0 })
    try {
      await upload.mutateAsync({
        lessonId: targetLessonId,
        file,
        signal: controller.signal,
        onProgress: (value) => setProgress({ name: file.name, value }),
      })
      toast.success('Vídeo enviado. Agora ele está sendo processado.')
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        toast.info('Envio cancelado.')
      } else {
        toast.error(videoErrorMessage(error))
      }
      throw error
    } finally {
      abortRef.current = null
      setProgress(null)
      onUploadingChange?.(false)
    }
  }

  async function send(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    const invalid = validateVideo(file)
    if (invalid) {
      toast.error(invalid)
      return
    }
    if (!lessonId) {
      setPreparingName(file.name)
      onUploadingChange?.(true)
      let createdId: string | undefined
      try {
        createdId = await onFileSelected?.(file)
        if (!createdId) throw new Error('Não foi possível criar a aula.')
        await sendFile(createdId, file)
      } catch {
        setPreparingName(null)
        onUploadingChange?.(false)
      } finally {
        if (createdId) onLessonReady?.(createdId)
      }
      return
    }
    try {
      await sendFile(lessonId, file)
    } catch {
      // O aviso fica em sendFile.
    }
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    void send(event.target.files)
    event.target.value = ''
  }

  async function handleRetry() {
    if (!lessonId) return
    try {
      await retry.mutateAsync(lessonId)
    } catch (error) {
      toast.error(videoErrorMessage(error))
    }
  }

  async function handleRemove() {
    if (!lessonId) return
    try {
      await remove.mutateAsync(lessonId)
      toast.success('Vídeo removido.')
      setPendingAction(null)
    } catch (error) {
      toast.error(videoErrorMessage(error))
    }
  }

  function confirmPendingAction() {
    if (pendingAction === 'replace') {
      setPendingAction(null)
      replaceInputRef.current?.click()
      return
    }
    if (pendingAction === 'remove') void handleRemove()
  }

  const replaceInput = (
    <input
      ref={replaceInputRef}
      type="file"
      accept={VIDEO_ACCEPT}
      className={visuallyHiddenInput}
      tabIndex={-1}
      aria-hidden
      onChange={onPick}
    />
  )

  if (progress) {
    return (
      <div className={videoField} data-testid="members-video-uploading">
        <VideoBusyStage
          phase="uploading"
          title="Enviando vídeo"
          fileName={progress.name}
          hint="Não feche esta aula até o envio terminar."
          progress={progress.value}
          action={
            <Button
              variant="text"
              size="sm"
              leftIcon={<X size={14} />}
              onClick={() => abortRef.current?.abort()}
            >
              Cancelar envio
            </Button>
          }
        />
      </div>
    )
  }

  if (!lesson) {
    if (preparingName) {
      return (
        <div className={videoField} data-testid="members-video-uploading">
          <VideoBusyStage
            phase="preparing"
            title="Criando a aula"
            fileName={preparingName}
            hint="O vídeo entra em seguida."
          />
        </div>
      )
    }
    return (
      <div className={videoField}>
        <FileDropzone
          id="members-lesson-video-new"
          label="Vídeo da aula"
          accept={VIDEO_ACCEPT}
          helperText="MP4, MOV ou WEBM de até 1 GB. A aula é criada ao enviar o vídeo."
          onChange={onPick}
        />
      </div>
    )
  }

  if (!video || video.status === 'UPLOADING') {
    return (
      <div className={videoField}>
        {video?.status === 'UPLOADING' ? (
          <Banner tone="warning" title="O envio não terminou">
            Envie o vídeo de novo para continuar.
          </Banner>
        ) : null}
        <FileDropzone
          id={`members-lesson-video-${lesson.id}`}
          label="Vídeo da aula"
          accept={VIDEO_ACCEPT}
          helperText="MP4, MOV ou WEBM de até 1 GB. Um vídeo por aula."
          onChange={onPick}
        />
      </div>
    )
  }

  if (video.status === 'PROCESSING') {
    return (
      <div className={videoField} data-testid="members-video-processing">
        <VideoBusyStage
          phase="processing"
          title="Processando vídeo"
          fileName={video.fileName}
          hint=""
        />
      </div>
    )
  }

  if (video.status === 'FAILED') {
    return (
      <div className={videoField} data-testid="members-video-failed">
        {replaceInput}
        <Banner
          tone="danger"
          title="Não foi possível processar o vídeo"
          action={
            <div className={videoActions}>
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<RefreshCw size={14} />}
                disabled={retry.isPending}
                onClick={() => void handleRetry()}
              >
                Tentar de novo
              </Button>
              <Button
                size="sm"
                variant="text"
                leftIcon={<Upload size={14} />}
                onClick={() => replaceInputRef.current?.click()}
              >
                Enviar outro vídeo
              </Button>
            </div>
          }
        >
          Confira se o arquivo abre no seu computador e tente de novo.
        </Banner>
      </div>
    )
  }

  return (
    <div className={videoField} data-testid="members-video-ready">
      {replaceInput}
      {video.playbackUrl ? (
        <HlsVideoPlayer
          src={apiUrl(video.playbackUrl)}
          title={`Prévia de ${lesson.title}`}
          requestHeaders={apiAuthHeaders}
        />
      ) : null}
      <div className={videoStatusRow}>
        <div className={fileMeta} style={{ flex: 1 }}>
          <strong>{video.fileName}</strong>
          <span>
            {video.durationSeconds !== null
              ? `${formatVideoDuration(video.durationSeconds)} · `
              : ''}
            {formatFileSize(video.sizeBytes)} · {video.renditions.join(', ')}
          </span>
        </div>
        <div className={videoActions}>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Upload size={14} />}
            onClick={() => setPendingAction('replace')}
          >
            Trocar vídeo
          </Button>
          <Button
            size="sm"
            variant="text"
            leftIcon={<Trash2 size={14} />}
            disabled={remove.isPending}
            onClick={() => setPendingAction('remove')}
          >
            Remover vídeo
          </Button>
        </div>
      </div>
      <DeleteConfirmDialog
        open={pendingAction !== null}
        title={pendingAction === 'replace' ? 'Trocar vídeo' : 'Remover vídeo'}
        description={
          pendingAction === 'replace'
            ? `Substituir o vídeo de ${lesson.title}? O processamento recomeça do zero.`
            : `Remover o vídeo de ${lesson.title}? A aula fica sem vídeo até você enviar outro.`
        }
        confirmLabel={pendingAction === 'replace' ? 'Trocar vídeo' : 'Remover vídeo'}
        pendingLabel={pendingAction === 'replace' ? 'Abrindo...' : 'Removendo...'}
        pending={remove.isPending}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => {
          confirmPendingAction()
        }}
      />
    </div>
  )
}
